import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { DashboardCardComponent } from '../dashboard-card/dashboard-card.component';

export interface DashboardTableMetricStack {
  kind: 'metric-stack';
  rows: Array<{ label: string; value: string | number }>;
  total: { label?: string; value: string | number };
}

export type DashboardTableCellValue = string | number | DashboardTableMetricStack;

export interface DashboardTableColumn<T = unknown> {
  key: string;
  label: string;
  align?: 'start' | 'center' | 'end';
  width?: string;
  hideOnMobile?: boolean;
  hideOnDesktop?: boolean;
  cellClass?: string | ((row: T) => string);
  value?: keyof T | ((row: T) => DashboardTableCellValue | null | undefined);
}

@Component({
  selector: 'app-dashboard-table-card',
  standalone: true,
  imports: [CommonModule, MatPaginatorModule, DashboardCardComponent],
  host: { '[attr.title]': 'null' },
  templateUrl: './dashboard-table-card.component.html',
  styleUrl: './dashboard-table-card.component.scss'
})
export class DashboardTableCardComponent<T = unknown> {
  @Input({ required: true }) title = '';
  @Input({ required: true }) icon = '';
  @Input() accentColor = '#1976d2';
  @Input() badge: string | number | null = null;
  @Input() badgeTooltip = '';
  @Input() titleLink: unknown[] | string | null = null;
  @Input() titleQueryParams: Record<string, unknown> | null = null;
  @Input() rows: T[] = [];
  @Input() columns: DashboardTableColumn<T>[] = [];
  @Input() isLoading = false;
  @Input() errorMessage: string | null = null;
  @Input() emptyIcon = 'table_rows';
  @Input() emptyMessage = 'Sin datos para mostrar';
  @Input() pageSize = 7;
  @Input() showPaginator = true;
  @Input() showFooter = false;
  @Input() contentPadding: 'none' | 'sm' | 'md' = 'none';
  @Output() rowClick = new EventEmitter<T>();
  @Output() pageChange = new EventEmitter<PageEvent>();
  @Output() retry = new EventEmitter<void>();

  pageIndex = 0;

  get displayedRows(): T[] {
    if (!this.showPaginator) {
      return this.rows;
    }

    const startIndex = this.pageIndex * this.pageSize;
    return this.rows.slice(startIndex, startIndex + this.pageSize);
  }

  get shouldShowFooter(): boolean {
    return this.showFooter || (this.showPaginator && this.rows.length > this.pageSize);
  }

  getCellValue(row: T, column: DashboardTableColumn<T>): DashboardTableCellValue {
    const valueSource = column.value ?? column.key;
    const value = typeof valueSource === 'function'
      ? valueSource(row)
      : (row as Record<string, unknown>)[valueSource as string];

    if (value === null || value === undefined) {
      return '-';
    }

    return typeof value === 'number' || typeof value === 'string'
      ? value
      : value as DashboardTableMetricStack;
  }

  getCellClass(row: T, column: DashboardTableColumn<T>): string {
    const classes = [
      `dashboard-table__cell--${column.align ?? 'start'}`,
      column.hideOnMobile ? 'dashboard-table__cell--mobile-hidden' : '',
      column.hideOnDesktop ? 'dashboard-table__cell--desktop-hidden' : '',
      typeof column.cellClass === 'function' ? column.cellClass(row) : column.cellClass ?? '',
    ];

    return classes.filter(Boolean).join(' ');
  }

  getHeaderClass(column: DashboardTableColumn<T>): string {
    const classes = [
      `dashboard-table__cell--${column.align ?? 'start'}`,
      column.hideOnMobile ? 'dashboard-table__cell--mobile-hidden' : '',
      column.hideOnDesktop ? 'dashboard-table__cell--desktop-hidden' : '',
      typeof column.cellClass === 'string' ? column.cellClass : '',
    ];

    return classes.filter(Boolean).join(' ');
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageChange.emit(event);
  }

  isMetricStackValue(value: DashboardTableCellValue): value is DashboardTableMetricStack {
    return typeof value === 'object' && value !== null && value.kind === 'metric-stack';
  }
}
